// Anita Week 3 / Day 4 — Data, Tables, Graphs and Weather.
// Content is supplied verbatim by the curriculum author; this file only places
// that content into the existing Nimny Lab lesson schema. No pedagogy is invented.
// SAFETY: weather observation only from a window or with a parent. Never alone outside.
// No thunderstorm / road / pool / balcony observation alone.

const day4 = {
  id: 'day-4',
  studentId: 'anita',
  weekId: 'week-3',
  dayId: 'day-4',
  status: 'active',
  title: 'Данные, таблицы, графики и погода / Data, Tables, Graphs and Weather',
  shortTitle: 'Данные и погода / Data and Weather',
  theme:
    'Анита учится собирать данные, читать таблицу, сравнивать числа и делать простой график погоды. / Anita learns to collect data, read a table, compare numbers, and make a simple weather graph.',
  estimatedTimeLabel: '60–90 минут / 60–90 minutes',
  lessonTiming: { minMinutes: 60, maxMinutes: 90, breaks: 2 },
  bilingualMode: 'ru-primary-en-helper',
  courseLayers: ['reading', 'math', 'science', 'data', 'weather', 'spelling', 'creative', 'speech'],
  readingLevel: 'grade-2',
  nimnyIntro:
    'Нимни открывает окно лаборатории и смотрит на небо. Сегодня Анита узнает, что погоду можно не только заметить, но и записать: солнечно, облачно, дождь, температура, таблица и график. / Nimny opens the lab window and looks at the sky. Today Anita learns that weather can be observed and recorded.',
  goals: [
    'Понять, что такое данные. / Understand data.',
    'Читать простую таблицу погоды. / Read a simple weather table.',
    'Сравнивать температуру и количество дней. / Compare temperature and number of days.',
    'Делать простой график. / Make a simple graph.',
    'Записать безопасное наблюдение погоды. / Record a safe weather observation.',
    'Объяснить вывод родителю. / Explain a conclusion to parent.'
  ],
  vocabulary: [
    { term: 'данные / data', definition: 'записанные результаты наблюдений. / recorded observation results.' },
    { term: 'таблица / table', definition: 'место, где данные стоят по строкам и столбцам. / a grid of rows and columns.' },
    { term: 'график / graph', definition: 'рисунок данных. / a picture of data.' },
    { term: 'погода / weather', definition: 'что происходит на улице: солнце, облака, дождь. / what is outside: sun, clouds, rain.' },
    { term: 'температура / temperature', definition: 'насколько тепло или холодно. / how warm or cold it is.' },
    { term: 'солнечно / sunny', definition: 'светит солнце. / the sun is shining.' },
    { term: 'облачно / cloudy', definition: 'много облаков. / many clouds.' },
    { term: 'дождь / rain', definition: 'вода падает с неба. / water falls from the sky.' },
    { term: 'вывод / conclusion', definition: 'что я понял после данных. / what I understood after the data.' }
  ],
  onlineTasks: [
    {
      id: 'reading-words',
      type: 'text',
      title: 'Слова данных / Data Words',
      prompt: 'Прочитай по-русски:\nданные\nтаблица\nграфик\nпогода\nтемпература\nсолнечно\nоблачно\nдождь\nвывод',
      ruPrimaryText: 'Прочитай 9 русских слов данных и погоды.',
      enHelpText: 'Read the Russian data and weather words first. English is only for help.',
      hint: 'Слово «температура» можно читать по частям. / Read longer words by syllables.',
      answer: 'Анита пробует читать русские слова. / Anita attempts the Russian reading.'
    },
    {
      id: 'reading-sentences',
      type: 'text',
      title: 'Маленькое чтение / Short Reading',
      prompt: 'Прочитай по-русски:\nЯ могу записать данные.\nТаблица помогает сравнивать.\nГрафик показывает результат.',
      ruPrimaryText: 'Прочитай 3 короткие фразы по-русски.',
      enHelpText: 'I can record data. A table helps compare. A graph shows the result.',
      hint: 'Сначала попробуй сама. / Try it first.',
      answer: 'Анита пробует читать 3 фразы. / Anita attempts the short reading.'
    },
    {
      id: 'math-weather-table',
      type: 'text',
      title: 'Таблица погоды / Weather Table',
      prompt:
        'Прочитай таблицу погоды Нимни:\nДень 1 — 20°C — солнечно\nДень 2 — 18°C — облачно\nДень 3 — 22°C — солнечно\nДень 4 — 17°C — дождь\nДень 5 — 21°C — солнечно\n\nОтветь:\n1. В какой день была самая высокая температура?\n2. В какой день была самая низкая температура?\n3. На сколько 22°C больше, чем 17°C?\n4. Сколько солнечных дней было?',
      ruPrimaryText: 'Прочитай таблицу погоды за 5 дней и ответь на 4 вопроса.',
      enHelpText: 'Read the weather table for 5 days and answer 4 questions.',
      answer: '1) День 3. 2) День 4. 3) 22 − 17 = 5°C. 4) 3 солнечных дня.'
    },
    {
      id: 'math-weather-count',
      type: 'text',
      title: 'Считаем погоду / Count the Weather',
      prompt:
        'За неделю Нимни записала:\nсолнечно — 3 дня\nоблачно — 2 дня\nдождь — 1 день\nветрено — 1 день\n\nОтветь:\n1. Сколько дней всего?\n2. Сколько дней НЕ были солнечными?\n3. На сколько солнечных дней больше, чем дождливых?',
      ruPrimaryText: 'Посчитай дни погоды и ответь на 3 вопроса.',
      enHelpText: 'Count the weather days: sunny 3, cloudy 2, rain 1, windy 1. Answer 3 questions.',
      answer: '1) 3 + 2 + 1 + 1 = 7. 2) 2 + 1 + 1 = 4. 3) 3 − 1 = 2.'
    },
    {
      id: 'data-bar-graph',
      type: 'text',
      title: 'Мини-график / Mini Bar Graph',
      prompt:
        'Сделай простой график словами или в тетради.\nДанные:\nсолнечно — 3\nоблачно — 2\nдождь — 1\n\nНарисуй столько квадратиков:\nсолнечно: □ □ □\nоблачно: □ □\nдождь: □\n\nОтветь:\nКакая погода была чаще всего?',
      ruPrimaryText: 'Сделай мини-график из квадратиков и ответь, какая погода была чаще всего.',
      enHelpText: 'Make a small box-graph: sunny 3, cloudy 2, rain 1. Answer which weather was most common.',
      answer: 'Солнечно было чаще всего.'
    },
    {
      id: 'science-weather-observation',
      type: 'text',
      title: 'Безопасное наблюдение / Safe Weather Observation',
      prompt:
        'Посмотри в окно или спроси родителя.\nНе выходи одна на улицу для этого задания.\n\nЗапиши:\nСегодня погода: ______.\nТемпература или ощущение: тепло / прохладно / жарко / холодно.\nНебо: солнечно / облачно / дождь / другое.',
      ruPrimaryText: 'Безопасно наблюдай погоду из окна или с родителем и запиши.',
      enHelpText: 'Look out a window or ask a parent. Do not go outside alone. Record the weather, temperature feel and sky.',
      hint: 'Только из окна или с родителем. Не выходи одна. / From a window or with a parent only.',
      answer: 'Open-ended. Parent checks that the observation is safe and done from a safe place.'
    },
    {
      id: 'spelling-data',
      type: 'text',
      title: 'Правописание / Spelling',
      prompt: 'Напиши:\nданные\nпогода\nграфик\nвывод\n\nВставь буквы:\nданн_е\nпог_да\nгр_фик\nвыв_д',
      ruPrimaryText: 'Напиши 4 слова и вставь пропущенные буквы.',
      enHelpText: 'Write 4 words: data, weather, graph, conclusion. Then fill the missing letters.',
      answer: 'данные, погода, график, вывод.'
    },
    {
      id: 'creative-weather-chart',
      type: 'text',
      title: 'Моя таблица погоды / My Weather Chart',
      prompt:
        'Сделай маленькую таблицу погоды.\nНарисуй 3 значка:\nсолнце\nоблако\nдождь\n\nНапиши:\nСегодня ______.\nМой вывод: ______.',
      ruPrimaryText: 'Нарисуй 3 значка погоды и напиши один вывод.',
      enHelpText: 'Draw 3 weather icons (sun, cloud, rain) and write one conclusion.',
      answer: 'Open-ended. Accept if Anita draws weather symbols and writes one conclusion.'
    },
    {
      id: 'speech-weather-explain',
      type: 'text',
      title: 'Объясни родителю / Explain to Parent',
      prompt: 'Скажи родителю по-русски:\nЯ записала данные.\nЯ посмотрела таблицу.\nМой вывод: ______ было чаще всего.',
      ruPrimaryText: 'Объясни родителю по-русски данные и вывод.',
      enHelpText: 'Tell a parent in Russian: I recorded data. I read the table. My conclusion: ___ was most common.',
      answer: 'Accept if Anita explains data and conclusion in Russian with support.'
    }
  ],
  notebookInstructions: [
    'Если нет принтера:',
    '1. Напиши слова дня.',
    '2. Прочитай 3 фразы.',
    '3. Реши задачи по таблице погоды.',
    '4. Нарисуй простой график из квадратиков.',
    '5. Посмотри в окно или спроси родителя о погоде.',
    '6. Нарисуй 3 значка погоды.',
    '7. Напиши один вывод.',
    '8. Объясни результат родителю.'
  ],
  printWorksheet: {
    title: 'Данные, таблицы, графики и погода / Data, Tables, Graphs and Weather',
    subtitle: 'Неделя 3 · День 4 / Week 3 · Day 4',
    pages: [
      {
        title: 'Маршрут дня / Route',
        blocks: [
          { type: 'text', text: 'Имя: ______________  Дата: ______________ / Name: __________  Date: __________' },
          { type: 'text', text: 'Миссия: Нимни записывает погоду как маленький учёный. Сегодня ты читаешь таблицу, считаешь данные и делаешь график. / Mission: Nimny records weather like a young scientist.' },
          { type: 'text', text: 'Слова дня / Words of the day:' },
          { type: 'list', items: [
            'данные / data',
            'таблица / table',
            'график / graph',
            'погода / weather',
            'температура / temperature',
            'солнечно / sunny',
            'облачно / cloudy',
            'дождь / rain',
            'вывод / conclusion'
          ] },
          { type: 'text', text: 'Маленькое чтение — прочитай и поставь галочку: / Short reading — read and tick:' },
          { type: 'list', items: [
            '☐ Я могу записать данные.',
            '☐ Таблица помогает сравнивать.',
            '☐ График показывает результат.'
          ] },
          { type: 'text', text: 'English help (only if hard): I can record data. A table helps compare. A graph shows the result.' }
        ]
      },
      {
        title: 'Таблица и график / Table and Graph',
        blocks: [
          { type: 'text', text: 'Таблица погоды Нимни / Nimny\'s weather table:' },
          { type: 'list', items: [
            'День 1 — 20°C — солнечно',
            'День 2 — 18°C — облачно',
            'День 3 — 22°C — солнечно',
            'День 4 — 17°C — дождь',
            'День 5 — 21°C — солнечно'
          ] },
          { type: 'text', text: 'Математика по таблице / Math from the table:' },
          { type: 'list', items: [
            '1. Самая высокая температура: ______',
            '2. Самая низкая температура: ______',
            '3. 22°C − 17°C = ______',
            '4. Солнечных дней было: ______'
          ] },
          { type: 'text', text: 'Считаем погоду / Count the weather: солнечно — 3, облачно — 2, дождь — 1, ветрено — 1.' },
          { type: 'list', items: [
            '1. Всего дней: ______',
            '2. Не солнечных дней: ______',
            '3. На сколько солнечных больше, чем дождливых? ______'
          ] },
          { type: 'text', text: 'Мини-график — заштрихуй квадратики / Mini graph — shade the boxes:' },
          { type: 'list', items: [
            'солнечно: □ □ □',
            'облачно: □ □',
            'дождь: □'
          ] },
          { type: 'text', text: 'Какая погода была чаще всего? ____________________' }
        ]
      },
      {
        title: 'Наблюдение и вывод / Observation and Conclusion',
        blocks: [
          { type: 'text', text: 'Безопасное наблюдение — посмотри в окно или спроси родителя. Не выходи одна на улицу. / Safe observation — from a window or with a parent. Do not go outside alone.' },
          { type: 'list', items: [
            'Сегодня погода: ____________________',
            'Ощущение: тепло / прохладно / жарко / холодно',
            'Небо: солнечно / облачно / дождь / другое'
          ] },
          { type: 'text', text: 'Правописание / Spelling — напиши и вставь буквы:' },
          { type: 'list', items: [
            'данные  ______  данн_е',
            'погода  ______  пог_да',
            'график  ______  гр_фик',
            'вывод  ______  выв_д'
          ] },
          { type: 'text', text: 'Нарисуй 3 значка погоды и подпиши / Draw 3 weather icons and label:' },
          { type: 'text', text: '┌────────┐ ┌────────┐ ┌────────┐  │ солнце │ │ облако │ │  дождь │  └────────┘ └────────┘ └────────┘' },
          { type: 'text', text: 'Мой вывод: Сегодня ____________________ .  Чаще всего на неделе было ____________________ .' },
          { type: 'text', text: 'Parent Check:' },
          { type: 'list', items: [
            '☐ Прочитала слова.',
            '☐ Прочитала 3 фразы.',
            '☐ Решила задачи по таблице.',
            '☐ Сделала мини-график.',
            '☐ Записала безопасное наблюдение.',
            '☐ Сказала вывод родителю.'
          ] },
          { type: 'text', text: 'Notebook Mode fallback — если нет принтера, сделай эти задания в тетради.' }
        ]
      }
    ]
  },
  parentGuide: {
    goal: 'Анита понимает, что данные — это записанные наблюдения, а таблица и график помогают сравнивать.',
    mathGoal: 'Сложение, вычитание, сравнение чисел, чтение таблицы, простой график.',
    russianGoal: 'Прочитать слова данные, таблица, график, погода, температура, солнечно, облачно, дождь, вывод.',
    readingWritingGoal: 'Прочитать 3 фразы, написать 4 слова, заполнить наблюдение и вывод.',
    practicalGoal: 'Сделать безопасное наблюдение погоды и простой график.',
    parentTheoryCard: {
      title: 'Как объяснить данные, таблицу и график',
      purpose: 'Помочь родителю объяснить, что данные — это не просто числа, а записанные наблюдения.',
      keyMessage: 'Когда мы записываем наблюдения, мы можем сравнивать и делать вывод.',
      parentScript: [
        '«Данные — это то, что мы записали после наблюдения.»',
        '«Таблица помогает держать данные в порядке.»',
        '«График помогает увидеть результат глазами.»',
        '«Вывод — это что мы поняли после данных.»'
      ],
      practiceSteps: [
        'Прочитайте таблицу погоды вместе.',
        'Найдите самое большое и самое маленькое число.',
        'Посчитайте, сколько солнечных дней.',
        'Нарисуйте мини-график.',
        'Спросите: «Какой вывод?»'
      ],
      doNotSay: [
        'Не требовать идеального графика.',
        'Не превращать наблюдение в контрольную.',
        'Не отправлять ребёнка одну на улицу ради погоды.'
      ],
      paperOnlyNotes: [
        'Можно делать задание в тетради.',
        'Погоду можно наблюдать из окна.',
        'Можно использовать weather app только вместе с родителем, но не обязательно.'
      ]
    },
    whatThisLessonTeaches: 'Анита учится превращать наблюдение в данные, а данные — в вывод.',
    keyMessage: 'Данные помогают думать: я записываю, сравниваю и делаю вывод.',
    howToExplain: 'Скажите: «Если мы просто говорим ‘погода была разная’, это мало. Если мы записали дни и температуру, мы можем сравнить.»',
    exampleDialogue:
      'Parent: Где самая высокая температура?\nChild: День 3.\nParent: Как ты узнала?\nChild: В таблице 22°C.\nParent: Какой вывод?\nChild: Солнечных дней было больше всего.',
    readingSupport: 'Слова «температура», «солнечно», «облачно» можно читать по частям.',
    russianSpeechGoal: 'Анита говорит: «Я записала данные. Мой вывод: ___ было чаще всего.»',
    mathAnswerKey:
      'math-weather-table: 1) День 3. 2) День 4. 3) 5°C. 4) 3 солнечных дня.\nmath-weather-count: 1) 7. 2) 4. 3) 2.\ndata-bar-graph: солнечно.',
    spellingAnswerKey: 'данные\nпогода\nграфик\nвывод',
    answerKey: [
      { taskId: 'reading-words', answer: 'Попытка чтения 9 русских слов.' },
      { taskId: 'reading-sentences', answer: 'Попытка чтения 3 коротких фраз.' },
      { taskId: 'math-weather-table', answer: 'День 3; День 4; 5°C; 3.' },
      { taskId: 'math-weather-count', answer: '7; 4; 2.' },
      { taskId: 'data-bar-graph', answer: 'солнечно.' },
      { taskId: 'science-weather-observation', answer: 'Безопасное наблюдение из окна или с родителем.' },
      { taskId: 'spelling-data', answer: 'данные, погода, график, вывод.' },
      { taskId: 'creative-weather-chart', answer: '3 значка погоды и один вывод.' },
      { taskId: 'speech-weather-explain', answer: 'Объяснение данных и вывода по-русски.' }
    ],
    check: [
      'Анита прочитала слова и фразы.',
      'Анита решила задачи по таблице.',
      'Анита сделала мини-график.',
      'Анита записала безопасное наблюдение.',
      'Анита написала вывод.',
      'Анита объяснила вывод по-русски.'
    ],
    spellingCheck: 'Проверить данные, погода, график, вывод.',
    readingCheck: 'Главное — попытка чтения и понимание смысла.',
    practicalCheck: 'Наблюдение безопасное, без самостоятельного выхода на улицу.',
    drawingCriteria: 'Есть 3 значка погоды и один вывод.',
    commonMistakes: [
      '«Данные — это просто числа.» Правильно: «Данные — это записанные наблюдения.»',
      '«График должен быть красивым.» Правильно: «График должен показывать данные.»',
      '«Нужно выйти на улицу одной.» Правильно: «Наблюдаем безопасно: из окна или с родителем.»'
    ],
    discussionPrompts: [
      'Почему таблица помогает?',
      'Что график показывает быстрее, чем длинный текст?',
      'Какая погода была чаще всего?',
      'Какой вывод можно сделать?'
    ],
    ifEasy: 'Пусть Анита добавит ещё 2 дня погоды и пересчитает график.',
    ifDifficult: 'Оставить только 3 дня: солнечно, облачно, дождь.',
    rewardGuidance: 'Базовая награда — 3 нима. Бонус за аккуратный график или ясный вывод.',
    tokenReward: 'Нимы — это призовые токены курса, а не оплата.',
    parentOnlyNotes: 'Не требовать настоящей метеорологической точности. Цель — понять данные, таблицу, график и вывод.',
    safetyNotes: 'Не выходить одной на улицу для наблюдения погоды. Не наблюдать грозу, дорогу, воду или опасные места без родителя. Наблюдать из окна или вместе с родителем.'
  },
  reward: {
    unit: 'nim',
    unitName: 'ним',
    base: 3,
    max: 5,
    label: 'Награда за миссию',
    display: '3 нима',
    bonuses: [
      { id: 'neat-bar-graph', label: 'Бонус за аккуратный мини-график', amount: 1 },
      { id: 'clear-conclusion', label: 'Бонус за ясный вывод по-русски', amount: 1 }
    ]
  },
  completionRequirements: {
    requiredTaskIds: [
      'reading-words',
      'reading-sentences',
      'math-weather-table',
      'math-weather-count',
      'data-bar-graph',
      'science-weather-observation',
      'spelling-data',
      'creative-weather-chart',
      'speech-weather-explain'
    ],
    allowNotebookCompletion: true
  },
  nextLessonId: 'anita-week-3-day-5'
};

export default day4;
